/**
 * Settings — web port of the mobile Settings screen (apps/mobile/app/settings.tsx).
 *
 * design-fidelity-tier1 (Builder B): the screen now opens with the design's
 * profile hero (avatar initial + display name + farm affiliation), FICA
 * tier/status chips and an FICA-upgrade banner sourced from real endpoints.
 * (The harvest-summary stat grid was cut with independent harvests — pilot scope.)
 * The pre-existing sectioned list (Appearance, Language,
 * Account hub, About, role/userId, sign-out) is retained verbatim below the new
 * hero; sign-out is now wrapped in a confirm Dialog per the design.
 *
 * Data sources (all real, react-query / orval):
 *   - GET /me/profile      → displayName (avatar initial + hero name)
 *   - GET /me/farm-profile → cooperativeName (affiliation line)
 *   - GET /kyc/me          → ficaStatus + ficaTier (hero chips + upgrade banner)
 * The farm-profile hook 404/403s for a non-farmer (coop_admin); the
 * UI degrades to the JWT-only profile rows in that case rather than erroring.
 *
 * Degradations (no endpoint exists yet):
 *   - Joined date: now shown (#12 — GET /me/profile gained joinedAt). Read
 *     defensively until the post-deploy orval regen types it on the client.
 *   - Notification/accessibility prefs: there is no prefs endpoint, so the
 *     Switch rows are local-only UI (honestly section-labelled "Preferences" and
 *     not persisted) — kept for the design's switch-row fidelity.
 *
 * Language: web has react-i18next wired (main.tsx). The picker also best-effort
 * persists to the server via PATCH /me/profile ({preferredLang}); the
 * localStorage `fse_lang` write stays the local source of truth.
 */
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  ColorModeToggle,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Icon,
  LanguagePicker,
  ListItem,
  Stack,
  Switch,
  Text,
  mdSysColor,
  radius,
  useColorMode,
} from '@fse/ui';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { readAccessTokenClaims } from '../api/client';
import { useHealthControllerLiveness } from '../api/generated/health/health';
import { useKycControllerGetMe } from '../api/generated/kyc/kyc';
import { meControllerUpdateProfile } from '../api/generated/me/me';
import { useMeControllerGetFarmProfile, useMeControllerGetProfile } from '../api/generated/me/me';

// --------------------------------------------------------------------------
// Static maps — mirror the mobile screen + the PREF_LANGS map used elsewhere
// in the web app (_app.farmers.new.tsx / _app.pending-reviews.tsx). Keep the
// labels in sync if those change.
// --------------------------------------------------------------------------

const SUPPORTED_LANGUAGES = ['en', 'xi', 'nso', 'ts'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  xi: 'isiXhosa',
  nso: 'Sepedi',
  ts: 'Xitsonga',
};

// LanguagePicker shows the native script first + an English subtitle. We keep
// the same labels as before — `native` is the script name, `english` is the
// language code shown beneath so the original "(code)" hint is preserved.
const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((code) => ({
  code,
  native: LANGUAGE_LABELS[code],
  english: code,
}));

// Maps a role enum to its i18n key under `settings.role.*`. Resolved via t() at
// render so the displayed label is translatable (TD-072); the enum keys remain
// logic values, not user copy.
const ROLE_LABEL_KEY: Record<string, string> = {
  coop_admin: 'settings.role.coopAdmin',
  admin: 'settings.role.admin',
  farmer: 'settings.role.farmer',
  sysCollab: 'settings.role.sysCollab',
};

// localStorage key for the persisted UI language.
const LANG_KEY = 'fse_lang';

function readStoredLang(): SupportedLanguage {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(LANG_KEY);
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(stored ?? '')
    ? (stored as SupportedLanguage)
    : 'en';
}

/**
 * Sign-out — replicated from _app.tsx (we don't import it to avoid coupling a
 * page to the layout module). Clear tokens then hard-navigate so the React
 * tree fully tears down and no stale TanStack Query cache bleeds into the next
 * session.
 */
function signOut() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('fse_access_token');
    window.localStorage.removeItem('fse_refresh_token');
    window.location.assign('/login');
  }
}

export function SettingsScreen() {
  const { t } = useTranslation();
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];

  // TD-114: surface the running API build (commit) so you can see what's live.
  const healthQuery = useHealthControllerLiveness();
  const build = (healthQuery.data as { commit?: string } | undefined)?.commit ?? '—';

  const claims = readAccessTokenClaims();
  const role = claims?.role;
  const roleLabel = role
    ? role in ROLE_LABEL_KEY
      ? t(ROLE_LABEL_KEY[role])
      : role
    : '—';

  // "Farmer-like" = G1 individual farmer + G2 group lead. Gates the farmer-only
  // Account rows + the farm hero data (those endpoints 403/404 a coop admin).
  const isFarmerLike = role === 'farmer' || role === 'group_lead';

  // Profile / farm / KYC — real endpoints. Farm is only
  // meaningful for farmers; gate the fetch so a coop_admin doesn't 404-spam.
  const profileQuery = useMeControllerGetProfile();
  const farmQuery = useMeControllerGetFarmProfile({ query: { enabled: isFarmerLike } });
  const kycQuery = useKycControllerGetMe();

  const displayName = profileQuery.data?.displayName ?? '';
  const avatarInitial = (displayName.trim()[0] ?? '?').toUpperCase();

  // joinedAt is on GET /me/profile (#12) and now typed on MyProfileDto (TD-124
  // regen) — read it directly off the generated query result.
  const joinedAt = profileQuery.data?.joinedAt;
  const joinedLabel = joinedAt
    ? new Date(joinedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    : null;

  const farm = farmQuery.data;
  const affiliation = farm?.cooperativeName
    ? farm.cooperativeName
    : farm?.principalType === 'individual'
      ? t('settings.hero.independentFarmer', 'Independent farmer')
      : null;

  const kyc = kycQuery.data;
  const ficaStatus = kyc?.ficaStatus;
  const ficaTier = kyc?.ficaTier;
  const ficaVerified = ficaStatus === 'verified';

  // --------------------------------------------------------------------------
  // Account hub rows (retained) — built as data so the trailing divider can be
  // dropped on whichever row ends up last after the farmer-only gating.
  // --------------------------------------------------------------------------
  const accountRows: { to: string; icon: string; label: string }[] = [
    { to: '/privacy', icon: 'shield', label: t('settings.account.privacy') },
    { to: '/change-pin', icon: 'pin', label: t('settings.account.changePin') },
    ...(isFarmerLike
      ? [{ to: '/my-activity', icon: 'history', label: t('settings.account.myActivity') }]
      : []),
    { to: '/my-data', icon: 'download', label: t('settings.account.myData') },
    ...(isFarmerLike
      ? [{ to: '/my-farm', icon: 'agriculture', label: t('settings.account.myFarm') }]
      : []),
  ];

  const [lang, setLang] = useState<SupportedLanguage>(readStoredLang);

  // Local-only preference switches (no prefs endpoint — see header note).
  const [notifyOffers, setNotifyOffers] = useState(true);
  const [notifyPayments, setNotifyPayments] = useState(true);
  const [notifyTips, setNotifyTips] = useState(false);

  // Logout confirm dialog (design fidelity — destructive action gets a confirm).
  const [confirmLogout, setConfirmLogout] = useState(false);

  function onLangChange(code: string) {
    const next = code as SupportedLanguage;
    setLang(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANG_KEY, next);
    }
    // TD-079: best-effort server persist so the preference follows the user.
    void meControllerUpdateProfile({ preferredLang: next }).catch(() => {
      /* best-effort: ignore network/auth errors */
    });
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Text variant="headline-medium" as="h1">
        {t('settings.title')}
      </Text>

      {/* ── Profile hero (design fidelity) ─────────────────────────────────
          Avatar initial + display name + farm affiliation + FICA chips. */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 1,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: sys.secondaryContainer,
                color: sys.onSecondaryContainer,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text variant="display-small" color={sys.onSecondaryContainer}>
                {avatarInitial}
              </Text>
            </Box>

            <Text variant="title-large" as="h2" align="center">
              {displayName || t('settings.hero.namePlaceholder', 'Your account')}
            </Text>

            {affiliation ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Icon name="agriculture" size={16} color={sys.onSurfaceVariant} />
                <Text variant="body-small" color={sys.onSurfaceVariant}>
                  {affiliation}
                </Text>
              </Box>
            ) : null}

            {joinedLabel ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Icon name="schedule" size={16} color={sys.onSurfaceVariant} />
                <Text variant="body-small" color={sys.onSurfaceVariant}>
                  {t('settings.hero.joined', {
                    date: joinedLabel,
                    defaultValue: 'Member since {{date}}',
                  })}
                </Text>
              </Box>
            ) : null}

            {/* FICA chips — only when we have a KYC record to report. */}
            {kyc ? (
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Chip
                  label={
                    ficaVerified
                      ? t('settings.fica.tierVerified', {
                          tier: ficaTier ?? 1,
                          defaultValue: 'FICA Tier {{tier}}',
                        })
                      : t('settings.fica.statusUnverified', 'FICA not verified')
                  }
                  color={ficaVerified ? 'success' : 'neutral'}
                  leadingIcon={ficaVerified ? 'verified' : 'shield'}
                />
                {ficaStatus === 'submitted' ? (
                  <Chip
                    label={t('settings.fica.statusPending', 'Review pending')}
                    color="neutral"
                    leadingIcon="schedule"
                  />
                ) : null}
              </Box>
            ) : null}
          </Box>
        </CardContent>
      </Card>

      {/* ── FICA upgrade banner — shown when not verified ──────────────────── */}
      {kyc && !ficaVerified ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            p: 2,
            borderRadius: `${radius.cornerLarge}px`,
            bgcolor: sys.tertiaryContainer,
            color: sys.onTertiaryContainer,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="shield" size={26} fill color={sys.onTertiaryContainer} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Text variant="title-small" color={sys.onTertiaryContainer}>
              {t('settings.fica.bannerTitle', 'Finish FICA verification')}
            </Text>
            <Box sx={{ mt: 0.25 }}>
              <Text variant="body-small" color={sys.onTertiaryContainer}>
                {t(
                  'settings.fica.bannerBody',
                  'Verify your identity to unlock higher payout limits.',
                )}
              </Text>
            </Box>
          </Box>
        </Box>
      ) : null}

      {/* Profile — read-only role + userId, sourced from the JWT claims. */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 1 }}>
            <Text variant="title-medium" as="h2">
              {t('settings.profile.title')}
            </Text>
          </Box>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <ListItem
              leadingIcon="badge"
              title={t('settings.profile.role')}
              trailing={
                <Text variant="body-medium" color="text.primary">
                  {roleLabel}
                </Text>
              }
            />
            <ListItem
              leadingIcon="fingerprint"
              title={t('settings.profile.userId')}
              trailing={
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  <Text variant="body-small" color="text.primary">
                    {claims?.sub ?? '—'}
                  </Text>
                </Box>
              }
              noDivider
            />
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Text variant="body-small" color="text.secondary">
              {t('settings.profile.editHint')}
            </Text>
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Link to="/profile" style={{ textDecoration: 'none' }}>
              <Button variant="outlined" size="small">
                {t('settings.profile.editButton')}
              </Button>
            </Link>
          </Box>
        </CardContent>
      </Card>

      {/* Appearance — reuse the shared colour-mode toggle from @fse/ui. */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 1 }}>
            <Text variant="title-medium" as="h2">
              {t('settings.appearance.title')}
            </Text>
          </Box>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <ListItem
              leadingIcon="dark_mode"
              title={t('settings.appearance.theme')}
              trailing={<ColorModeToggle />}
              noDivider
            />
          </Box>
        </CardContent>
      </Card>

      {/* Preferences — notification switch rows (design fidelity). Local-only:
          there is no prefs endpoint yet, so these are not persisted server-side.
          Labelled honestly so the lack of persistence isn't surprising. */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 1 }}>
            <Text variant="title-medium" as="h2">
              {t('settings.prefs.title', 'Notifications')}
            </Text>
          </Box>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <ListItem
              leadingIcon="sell"
              title={t('settings.prefs.offers', 'New offers')}
              supportingText={t('settings.prefs.offersHint', 'When a buyer makes an offer')}
              trailing={
                <Switch
                  checked={notifyOffers}
                  onChange={setNotifyOffers}
                  aria-label={t('settings.prefs.offers', 'New offers')}
                />
              }
            />
            <ListItem
              leadingIcon="payments"
              title={t('settings.prefs.payments', 'Payments')}
              supportingText={t('settings.prefs.paymentsHint', 'When money reaches your account')}
              trailing={
                <Switch
                  checked={notifyPayments}
                  onChange={setNotifyPayments}
                  aria-label={t('settings.prefs.payments', 'Payments')}
                />
              }
            />
            <ListItem
              leadingIcon="notifications"
              title={t('settings.prefs.tips', 'Market tips')}
              supportingText={t('settings.prefs.tipsHint', 'Weekly prices in your area')}
              trailing={
                <Switch
                  checked={notifyTips}
                  onChange={setNotifyTips}
                  aria-label={t('settings.prefs.tips', 'Market tips')}
                />
              }
              noDivider
            />
          </Box>
          <Box sx={{ mt: 1.5, fontStyle: 'italic' }}>
            <Text variant="body-small" color="text.secondary">
              {t('settings.prefs.localOnlyHint', 'Saved on this device for now.')}
            </Text>
          </Box>
        </CardContent>
      </Card>

      {/* Language */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 1.5 }}>
            <Text variant="title-medium" as="h2">
              {t('settings.language.title')}
            </Text>
          </Box>
          <Stack spacing={1.5}>
            <LanguagePicker
              options={LANGUAGE_OPTIONS}
              value={lang}
              onChange={onLangChange}
              aria-label={t('settings.language.ariaLabel')}
            />
            <Box sx={{ fontStyle: 'italic' }}>
              <Text variant="body-small" color="text.secondary">
                {t('settings.language.hint')}
              </Text>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Account — nav rows moved OFF the top-level sidebar (IA cleanup). */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 1 }}>
            <Text variant="title-medium" as="h2">
              {t('settings.account.title')}
            </Text>
          </Box>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {accountRows.map((row, idx) => (
              <AccountRow
                key={row.to}
                to={row.to}
                icon={row.icon}
                label={row.label}
                noDivider={idx === accountRows.length - 1}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* About */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 1 }}>
            <Text variant="title-medium" as="h2">
              {t('settings.about.title')}
            </Text>
          </Box>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 1.5 }}>
            <ListItem
              leadingIcon="agriculture"
              title={t('settings.about.appLabel')}
              trailing={
                <Text variant="body-medium" color="text.primary">
                  {t('settings.about.appName')}
                </Text>
              }
            />
            <ListItem
              leadingIcon="badge"
              title="Build"
              trailing={
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  <Text variant="body-small" color="text.primary">
                    {build}
                  </Text>
                </Box>
              }
              noDivider
            />
          </Box>
          <Text variant="body-medium" color="text.secondary">
            {t('settings.about.body')}
          </Text>
        </CardContent>
      </Card>

      <Divider />

      {/* Sign out — now behind a confirm Dialog (design fidelity). */}
      <Box>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => setConfirmLogout(true)}
          startIcon={<Icon name="logout" size={20} />}
        >
          {t('settings.signOut')}
        </Button>
      </Box>

      <Dialog open={confirmLogout} onClose={() => setConfirmLogout(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('settings.logout.confirmTitle', 'Log out of FSE?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'settings.logout.confirmBody',
              "You'll need your phone number and a new code to log back in.",
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmLogout(false)}>
            {t('settings.logout.cancel', 'Stay')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmLogout(false);
              signOut();
            }}
          >
            {t('settings.signOut')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * Account nav row — a TanStack `Link` (client-side nav) wrapping a static
 * `ListItem` with a leading icon, label, and trailing chevron. We wrap the
 * Link rather than using ListItem's own `href` so navigation stays in-SPA
 * (ListItem's href renders a plain anchor → full page reload).
 */
function AccountRow({
  to,
  icon,
  label,
  noDivider,
}: {
  to: string;
  icon: string;
  label: string;
  noDivider?: boolean;
}): ReactNode {
  // Trailing nav indicator uses `arrow_forward` (the registry has no
  // `chevron_right`; an unregistered name would render nothing — TD-093).
  return (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <ListItem
        leadingIcon={icon}
        title={
          <Text variant="body-medium" color="text.primary">
            {label}
          </Text>
        }
        trailing={<Icon name="arrow_forward" size={20} />}
        noDivider={noDivider}
      />
    </Link>
  );
}
